import { useState, useId, useMemo, useEffect, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { Modal, Button, StatusHint, Tabs } from '@indipia/ui-core';
import type { ExternalLinkSendProps, Recipient, SendResultItem } from './types';
import { createSendFormSchema, type SendFormValues } from './formSchema';
import { formatPhone } from './utils/phone';
import { RecipientArea } from './RecipientArea';
import { MessageForm } from './MessageForm';
import { SaveAfterSendDialog } from './SaveAfterSendDialog';
import { KakaoMessageCard } from './KakaoMessageCard';
import { useExternalLink } from './hooks/useExternalLink';

// "2026-06-26" → "만료일 : 2026년 6월 26일 까지 링크 접근 가능"
function formatExpiresAtHelper(dateStr: string): string {
  const [y, m, d] = dateStr.split('-');
  return `만료일 : ${y}년 ${Number(m)}월 ${Number(d)}일 까지 링크 접근 가능`;
}

export function ExternalLinkSend({
  sourceService,
  sourceAgent,
  resourceType,
  shareType,
  shareScope = 'READ',
  authType = 'NONE',
  showMessageForm = true,
  showPreview = false,
  sendLabel = '발송하기',
  sendDisabled = false,
  extraActions,
  onSend,
  onExpiresAtChange,
  onMessageChange,
  expiryNotice,
  customerExpiryNotice,
  resolveRecipientWorkTypes,
  customerOnlyDirectInput,
  onRecipientsChange,
  resolveResourceRef,
  initialVariables,
  resolveVariables,
  initialButtons,
  resolveButtons,
  dispatchBatchId,
  resolveRecipientVariables,
  resolvePreviewContent,
  sendOverride,
  onSent,
  initialRecipients,
  initialTitle,
  initialContent,
  initialExpiresAt,
}: ExternalLinkSendProps) {
  const today = new Date().toISOString().slice(0, 10);

  const keyPrefix = useId();

  const [recipients, setRecipients] = useState<Recipient[]>(() =>
    (initialRecipients ?? []).map((r, i) => ({
      ...r,
      key: `${keyPrefix}-init-${i}`,
      isNew: false,
    })),
  );
  const [recipientsError, setRecipientsError] = useState('');
  const [failedResults, setFailedResults] = useState<SendResultItem[]>([]);
  const recipientSectionRef = useRef<HTMLElement>(null);

  // 고객 / 반장님·거래처 탭 — 같은 화면에서 영역 내용만 바꾼다(D-09 §15.19 Tabs). 탭을 바꿔도 입력값은 유지된다.
  const [recipientTab, setRecipientTab] = useState<'CUSTOMER' | 'PARTNER'>('CUSTOMER');

  // 알림톡은 반장님·거래처 수신자에게만 보낸다 — 좌측(고객)은 [공유하기](링크 복사·카톡 공유)로 전달한다.
  const sendRecipients = recipients.filter((r) => r.recipientType !== 'CUSTOMER');

  // 수신자 관련 에러(1명 이상 필요 등)를 세팅함과 동시에 우측 "받는 대상" 카드로 스크롤·강조 —
  // 발송 버튼과 카드가 화면상 멀리 떨어져 있으면 에러 문구만으론 눈에 잘 안 띄어서 추가.
  function flagRecipientsError(msg: string) {
    setRecipientsError(msg);
    recipientSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  const schema = useMemo(() => createSendFormSchema(showMessageForm, expiryNotice == null), [showMessageForm, expiryNotice]);

  const methods = useForm<SendFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      expiresAt: initialExpiresAt ?? '',
      title: initialTitle ?? '',
      content: initialContent ?? '',
      customerTitle: '',
      customerContent: '',
    },
  });

  const { register, handleSubmit, watch, getValues, formState: { errors } } = methods;
  const expiresAtValue = watch('expiresAt') ?? '';

  useEffect(() => {
    if (expiresAtValue) onExpiresAtChange?.(expiresAtValue);
  }, [expiresAtValue, onExpiresAtChange]);

  // 제목·내용 변경 알림 — 마운트 직후 1회 + 값이 바뀔 때마다
  const titleValue = watch('title') ?? '';
  const contentValue = watch('content') ?? '';
  const onMessageChangeRef = useRef(onMessageChange);
  onMessageChangeRef.current = onMessageChange;
  useEffect(() => {
    if (showMessageForm) onMessageChangeRef.current?.({ title: titleValue, content: contentValue });
  }, [showMessageForm, titleValue, contentValue]);

  const { isSending, send, saveDialogItems, setSaveDialogItems, saveDialogOpen, setSaveDialogOpen } =
    useExternalLink({ sourceService, sourceAgent, resourceType, shareType, shareScope, authType, sendOverride });

  // 수신자 목록 변경 알림 — 미리보기 등 화면 연동용
  const onRecipientsChangeRef = useRef(onRecipientsChange);
  onRecipientsChangeRef.current = onRecipientsChange;
  useEffect(() => {
    onRecipientsChangeRef.current?.(recipients);
  }, [recipients]);

  function addRecipient(r: Omit<Recipient, 'key'>) {
    // 담당 공종을 쓰는 화면에서는 같은 파트너를 두 번 추가하지 못하게 한다(공종이 중복 발송되므로)
    if (resolveRecipientWorkTypes && r.recipientType !== 'CUSTOMER' && r.id != null
      && recipients.some((x) => x.recipientType === r.recipientType && x.id === r.id)) {
      flagRecipientsError('이미 추가된 수신자입니다.');
      return;
    }
    const key = `${keyPrefix}-${Date.now()}`;
    const needsWorkTypes = !!resolveRecipientWorkTypes && r.recipientType !== 'CUSTOMER';
    const added: Recipient = { ...r, key, ...(needsWorkTypes && { workTypesStatus: 'loading' as const }) };
    setRecipients((prev) => [...prev, added]);
    setRecipientsError('');
    if (needsWorkTypes) {
      resolveRecipientWorkTypes!(added)
        .then((workTypes) => {
          setRecipients((prev) => prev.map((x) => (x.key === key ? { ...x, workTypes, workTypesStatus: 'ready' as const } : x)));
        })
        .catch(() => {
          setRecipients((prev) => prev.map((x) => (x.key === key ? { ...x, workTypesStatus: 'error' as const } : x)));
        });
    }
  }

  function removeRecipient(key: string) {
    setRecipients((prev) => prev.filter((r) => r.key !== key));
  }

  // 담당 공종 제외 — 해당 공종은 발송에서 빠진다
  function removeWorkType(key: string, workTypeCode: string) {
    setRecipients((prev) => prev.map((r) => (
      r.key === key ? { ...r, workTypes: (r.workTypes ?? []).filter((w) => w.code !== workTypeCode) } : r
    )));
  }

  // 담당 공종이 정해지지 않은 반장·거래처가 있으면 발송을 막는다 — 값을 비운 채 보내지 않는다.
  function workTypeBlocker(): string | null {
    if (!resolveRecipientWorkTypes) return null;
    for (const r of sendRecipients) {
      if (r.recipientType === 'CUSTOMER') continue;
      if (r.workTypesStatus === 'loading') return '담당 공종을 불러오는 중입니다. 잠시 후 다시 시도해 주세요.';
      if (r.workTypesStatus === 'error') return `${r.name}의 담당 공종을 불러오지 못했습니다. 제거한 뒤 다시 추가해 주세요.`;
      if ((r.workTypes?.length ?? 0) === 0) return `${r.name}에게 보낼 공종이 없습니다. 수신자를 제거해 주세요.`;
    }
    return null;
  }

  // ── 발송/미리보기 공용 — variables·buttons 조립 ────────────────────────────
  // onSubmit(실제 발송)과 handlePreview가 반드시 같은 방식으로 조립해야 미리보기가
  // 실제 발송 값과 어긋나지 않는다. formOverride 미지정 시 getValues()(현재 폼 값)를 쓴다
  // — onSubmit은 검증 통과한 data를 그대로 넘겨 기존 동작과 100% 동일한 값을 만든다.
  async function buildVariables(
    recipient?: Recipient,
    formOverride?: { title: string; content: string },
  ): Promise<Record<string, string>> {
    const agentVariables = (await resolveVariables?.()) ?? initialVariables ?? {};
    const variables: Record<string, string> = { ...agentVariables };
    if (showMessageForm) {
      const { title, content } = formOverride ?? getValues();
      variables.title = title;
      variables.content = content;
    }
    // 수신자마다 값이 달라지는 템플릿이면(resolveRecipientVariables 지정 시) 해당 수신자 값으로 덮어쓴다.
    if (recipient && resolveRecipientVariables) {
      Object.assign(variables, resolveRecipientVariables(recipient, variables));
    }
    return variables;
  }

  async function buildButtons() {
    return (await resolveButtons?.()) ?? initialButtons ?? [];
  }

  // ── 미리보기 (F-05 알림톡 템플릿 조회 + #{변수} 치환) ──────────────────────
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewContent, setPreviewContent] = useState('');
  const [previewButtons, setPreviewButtons] = useState<string[]>([]);

  async function handlePreview() {
    if (sendRecipients.length === 0) {
      flagRecipientsError('반장님 · 거래처를 1명 이상 추가해 주세요.');
      return;
    }
    setRecipientsError('');
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError('');
    try {
      // 미리보기는 첫 번째 수신자 기준으로만 계산해서 보여준다 — 실제 발송은 수신자별로
      // 각각 나가지만(useExternalLink.send) 미리보기 화면은 카드 하나만 보여주면 되기 때문.
      const variables = await buildVariables(sendRecipients[0]);
      const buttons = await buildButtons();

      // 서버가 템플릿을 치환하는 규격(포토 등)에서는 발송 variables에 원본 값만 담으므로,
      // resolvePreviewContent로 완성 문구를 따로 만들어 미리보기에만 보여준다.
      // 미지정(스케줄 등 완성 문구를 variables.content에 그대로 담는 규격)이면 content를 그대로 쓴다.
      const previewText = resolvePreviewContent
        ? resolvePreviewContent(variables, sendRecipients[0])
        : variables.content;
      if (previewText) {
        setPreviewContent(previewText);
      } else {
        setPreviewError('미리보기할 메시지 내용이 없습니다.');
      }
      setPreviewButtons(buttons.map((b) => b.name));
    } catch {
      setPreviewError('미리보기를 불러오지 못했습니다.');
    } finally {
      setPreviewLoading(false);
    }
  }

  const onSubmit = async (data: SendFormValues) => {
    if (sendRecipients.length === 0) {
      flagRecipientsError('반장님 · 거래처를 1명 이상 추가해 주세요.');
      return;
    }
    const blocked = workTypeBlocker();
    if (blocked) {
      flagRecipientsError(blocked);
      return;
    }
    setRecipientsError('');
    setFailedResults([]);

    // 발송 시점에 공유 대상 키(resourceRef) 확보 (F-04 §6.1)
    let resourceRef: string;
    try {
      resourceRef = await resolveResourceRef();
    } catch {
      flagRecipientsError('공유할 내용을 준비하지 못했습니다. 다시 시도해 주세요.');
      return;
    }

    const variables = await buildVariables(undefined, { title: data.title, content: data.content });
    const buttons = await buildButtons();

    const sent = await send(resourceRef, sendRecipients, variables, data.expiresAt ? `${data.expiresAt}T23:59:59` : '', dispatchBatchId, buttons, resolveRecipientVariables);
    if (sent) {
      const { result, payload } = sent;
      onSend?.(result);
      if (result.failedCount > 0) {
        setFailedResults(result.results.filter((r) => !r.success));
      }
      onSent?.(result, payload);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <div className="space-y-4">

          {/* 고객 · 반장님·거래처 탭 — 각 탭: 링크 유효기간 → 받는 대상 → 메시지 작성 → 버튼 */}
          <Tabs
            tabs={[
              { label: '고객', value: 'CUSTOMER' },
              { label: '반장님 · 거래처', value: 'PARTNER' },
            ]}
            value={recipientTab}
            onChange={(v) => setRecipientTab(v as 'CUSTOMER' | 'PARTNER')}
          />

          {recipientTab === 'CUSTOMER' ? (
            // ── 고객 — [공유하기](링크 복사·카톡 공유)로 전달, 알림톡 발송 대상 아님 ──
            <>
              {customerExpiryNotice != null && <ExpiryCard>{customerExpiryNotice}</ExpiryCard>}

              <section className="rounded-xxl border border-border-default bg-surface-raised p-5">
                <RecipientArea
                  recipients={recipients}
                  onAdd={addRecipient}
                  onRemove={removeRecipient}
                  customerOnlyDirectInput={customerOnlyDirectInput}
                  types={['CUSTOMER']}
                />
                <div className="mt-3">
                  <StatusHint tone="info">고객에게는 [공유하기]로 링크를 전달해요. 여기 고객 · 메시지는 알림톡으로 발송되지 않아요.</StatusHint>
                </div>
              </section>

              {showMessageForm && (
                <MessageCard>
                  <MessageForm titleName="customerTitle" contentName="customerContent" />
                </MessageCard>
              )}

              {extraActions && <div className="flex gap-3 [&>*]:flex-1">{extraActions}</div>}
            </>
          ) : (
            // ── 반장님 · 거래처 — [알림톡 보내기] 발송 대상 + [공유하기] ──
            <>
              {/* 링크 유효기간 — expiryNotice 지정 시 읽기 전용 안내(서버 산정), 아니면 날짜 입력 */}
              {expiryNotice != null ? (
                <ExpiryCard>{expiryNotice}</ExpiryCard>
              ) : (
                <ExpiryCard>
                  <input
                    type="date"
                    min={today}
                    {...register('expiresAt')}
                    className={`w-full rounded-xxl border px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary-400 ${
                      errors.expiresAt ? 'border-error' : 'border-border-default'
                    }`}
                  />
                  {expiresAtValue && !errors.expiresAt && (
                    <p className="text-xs text-text-secondary">{formatExpiresAtHelper(expiresAtValue)}</p>
                  )}
                  {errors.expiresAt && (
                    <p className="text-xs text-error">{errors.expiresAt.message}</p>
                  )}
                </ExpiryCard>
              )}

              <section
                ref={recipientSectionRef}
                className={`rounded-xxl border bg-surface-raised p-5 transition-colors ${
                  recipientsError ? 'border-error' : 'border-border-default'
                }`}
              >
                <RecipientArea
                  recipients={recipients}
                  onAdd={addRecipient}
                  onRemove={removeRecipient}
                  onRemoveWorkType={resolveRecipientWorkTypes ? removeWorkType : undefined}
                  customerOnlyDirectInput={customerOnlyDirectInput}
                  types={['PARTNER', 'VENDOR']}
                />
                {recipientsError && (
                  <p className="mt-2 text-xs text-error">{recipientsError}</p>
                )}
              </section>

              {showMessageForm && (
                <MessageCard>
                  <MessageForm />
                </MessageCard>
              )}

              <div className="flex gap-3">
                {showPreview && (
                  <Button variant="secondary" type="button" onClick={() => void handlePreview()} className="flex-none">
                    미리보기
                  </Button>
                )}
                {extraActions}
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => void handleSubmit(onSubmit)()}
                  disabled={isSending || sendDisabled}
                  className="flex-1"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                  {isSending ? '발송 중…' : sendLabel}
                </Button>
              </div>
            </>
          )}

          {/* ── 발송 실패 결과 ─────────────────────────────────────── */}
          {failedResults.length > 0 && (
            <div className="space-y-1 rounded-xxl border border-error bg-surface-subtle p-4">
              <p className="text-sm font-medium text-error">일부 발송 실패</p>
              {failedResults.map((r, i) => (
                <p key={i} className="text-xs text-error">
                  {r.name} ({formatPhone(r.mobile)}) — {r.errorMsg ?? '알 수 없는 오류'}
                </p>
              ))}
            </div>
          )}

        </div>
      </FormProvider>

      {/* ── 전송 이후 저장 다이얼로그 ─────────────────────────────── */}
      {saveDialogOpen && (
        <SaveAfterSendDialog
          items={saveDialogItems}
          onChange={setSaveDialogItems}
          onClose={() => setSaveDialogOpen(false)}
        />
      )}

      {/* ── 알림톡 미리보기 ────────────────────────────────────────── */}
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="카카오 알림톡 미리보기">
        {previewLoading && <p className="text-sm text-text-secondary">불러오는 중…</p>}
        {!previewLoading && previewError && (
          <p className="text-sm text-error">{previewError}</p>
        )}
        {!previewLoading && !previewError && (
          <KakaoMessageCard content={previewContent} buttons={previewButtons} />
        )}
      </Modal>
    </>
  );
}

/** 링크 유효기간 카드 — 좌 · 우 영역 공용(기존 카드 마크업 그대로) */
function ExpiryCard({ children }: { children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-xxl border border-border-default bg-surface-raised p-5">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xxl bg-primary-100 text-primary-600">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <span className="text-sm font-semibold text-text-primary">링크 유효기간</span>
      </div>
      {children}
    </section>
  );
}

/** 메시지 작성 카드 — 좌 · 우 영역 공용(기존 카드 마크업 그대로) */
function MessageCard({ children }: { children: ReactNode }) {
  return (
    <section className="space-y-4 rounded-xxl border border-border-default bg-surface-raised p-5">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xxl bg-primary-100 text-primary-600">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
          </svg>
        </div>
        <span className="text-sm font-semibold text-text-primary">메시지 작성</span>
      </div>
      {children}
    </section>
  );
}
